import { ref } from 'vue';
import {
  getEncounterSessionsApi,
  getSessionAudioApi,
} from '@/api/recordingApi';

/**
 * Composable for managing multiple recording sessions per encounter:
 * session list, selection, switching, and on-demand audio fetch.
 *
 * @param {Object} options
 * @param {import('vue').Ref<string|number>} options.sessionId - Current session ID ref (from useRecording)
 * @param {import('vue').Ref<string>} options.tempRecordingUrl - Blob URL ref (from useRecording)
 * @param {Function} options.clearTempRecording - Clear temp recording (from useRecording)
 * @param {Function} options.getTranscription - Fetch transcription for current session (from useTranscription)
 * @param {Function} options.resetSummaryState - Reset summary state (from useTranscription)
 */
export function useSessionManager({
  sessionId,
  tempRecordingUrl,
  clearTempRecording,
  getTranscription,
  resetSummaryState,
}) {
  const allSessions = ref([]);
  const selectedSessionIndex = ref(0);
  const isAudioLoading = ref(false);

  /**
   * Initialize sessions from existing recordings (returning to a saved note)
   */
  const initializeFromRecordings = async (encounterNoteId, isLocalRecording) => {
    if (isLocalRecording) return;
    if (!encounterNoteId) return;

    try {
      const sessionsResponse = await getEncounterSessionsApi(encounterNoteId);
      const sessionsList = (sessionsResponse?.sessions || []).filter(s => s.status === 'completed');
      allSessions.value = sessionsList;
      const firstSession = sessionsList[0];
      if (!firstSession?.id) return;
      sessionId.value = firstSession.id;
      selectedSessionIndex.value = 0;
    } catch (error) {
      console.error('Failed to get encounter sessions:', error);
      return;
    }

    await getTranscription();
  };

  /**
   * Switch to a different recording session
   */
  const handleSelectSession = async (index) => {
    if (index === selectedSessionIndex.value) return;
    const session = allSessions.value[index];
    if (!session?.id) return;

    selectedSessionIndex.value = index;
    sessionId.value = session.id;

    // Reset current audio and summary state
    clearTempRecording();
    resetSummaryState();

    await getTranscription();
  };

  /**
   * Push a new session to the front of the list and select it
   */
  const pushSession = (newSessionId) => {
    allSessions.value.unshift({ id: newSessionId });
    selectedSessionIndex.value = 0;
    sessionId.value = newSessionId;
  };

  /**
   * Remove the first session from the list (e.g., failed import)
   */
  const removeFirstSession = () => {
    allSessions.value.shift();
    selectedSessionIndex.value = allSessions.value.length > 0 ? 0 : -1;
    if (allSessions.value.length > 0) {
      sessionId.value = allSessions.value[0].id;
    }
  };

  /**
   * Fetch audio on demand when user clicks "Play Recording"
   * Uses getSessionAudioApi for both regular and Zoom recordings
   */
  const fetchAudioOnDemand = async () => {
    if (tempRecordingUrl.value) return;
    if (!sessionId.value) return;

    isAudioLoading.value = true;
    try {
      const audioBlob = await getSessionAudioApi(sessionId.value);
      if (audioBlob) {
        tempRecordingUrl.value = URL.createObjectURL(audioBlob);
      }
    } catch (error) {
      console.error('Failed to fetch audio:', error);
    } finally {
      isAudioLoading.value = false;
    }
  };

  /**
   * Reset session list state
   */
  const resetSessions = () => {
    allSessions.value = [];
    selectedSessionIndex.value = 0;
  };

  return {
    // State
    allSessions,
    selectedSessionIndex,
    isAudioLoading,

    // Methods
    initializeFromRecordings,
    handleSelectSession,
    pushSession,
    removeFirstSession,
    fetchAudioOnDemand,
    resetSessions,
  };
}
